import React from "react";
import {
  Send,
  ShieldCheck,
  GitMerge,
  FileSearch,
  CheckCircle2,
  IndianRupee,
  TrendingUp,
  Sparkles,
} from "lucide-react";

const stages = [
  {
    id: "01",
    title: "Submitted",
    description: "DSA files the lead from the app — borrower details, loan type, ticket size. Under 2 minutes.",
    icon: <Send size={21} className="text-[var(--mint-green)] drop-shadow-[0_0_8px_rgba(46,229,162,0.4)]" strokeWidth={2.2} />,
  },
  {
    id: "02",
    title: "Verified",
    description: "Mobile, name and UPI/VPA checks run automatically. Timestamped. Turnaround: 4 minutes.",
    icon: <ShieldCheck size={21} className="text-[var(--mint-green)] drop-shadow-[0_0_8px_rgba(46,229,162,0.4)]" strokeWidth={2.2} />,
  },
  {
    id: "03",
    title: "Matched",
    description: "Routing decides in-house funding or a ranked list of partner NBFCs and banks. Instant.",
    icon: <GitMerge size={21} className="text-[var(--mint-green)] drop-shadow-[0_0_8px_rgba(46,229,162,0.4)]" strokeWidth={2.2} />,
  },
  {
    id: "04",
    title: "Reviewed",
    description: "Finverse credit desk or partner lender opens the file and asks for documents. 12–48 hours.",
    icon: <FileSearch size={21} className="text-[var(--mint-green)] drop-shadow-[0_0_8px_rgba(46,229,162,0.4)]" strokeWidth={2.2} />,
  },
  {
    id: "05",
    title: "Approved",
    description: "Sanction issued with terms. Rejections reroute to the next ranked lender the same day.",
    icon: <CheckCircle2 size={21} className="text-[var(--mint-green)] drop-shadow-[0_0_8px_rgba(46,229,162,0.4)]" strokeWidth={2.2} />,
  },
  {
    id: "06",
    title: "Disbursed",
    description: "Funds move to the borrower. Status updates to the DSA in real time. 1–5 working days.",
    icon: <IndianRupee size={21} className="text-[var(--mint-green)] drop-shadow-[0_0_8px_rgba(46,229,162,0.4)]" strokeWidth={2.2} />,
  },
  {
    id: "07",
    title: "Commission paid",
    description: "Payout is calculated on the disbursed amount and transferred on the next cycle.",
    icon: <TrendingUp size={21} className="text-[var(--mint-green)] drop-shadow-[0_0_8px_rgba(46,229,162,0.4)]" strokeWidth={2.2} />,
  },
];

function LeadTimeline() {
  return (
    <section
      id="pipeline-timeline"
      className="relative overflow-hidden bg-[#191e1c] section-spacing scroll-mt-20"
    >
      <div className="section-container relative z-10">
        
        {/* ================= SECTION HEADER ================= */}
        <div className="mx-auto max-w-[760px] text-center mb-12 sm:mb-16 lg:mb-20">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(46,229,162,0.1)] px-3.5 py-1 font-jakarta text-[12px] font-bold text-[var(--mint-green)] border border-[rgba(46,229,162,0.25)] shadow-xs mb-3.5">
            <Sparkles size={13} className="text-[var(--mint-green)]" />
            <span>HOW IT WORKS · 7 STAGES</span>
          </span>

          <h2 className="font-bricolage text-[30px] sm:text-[38px] md:text-[44px] font-semibold leading-[1.1] tracking-[-1px] text-white">
            From lead to payout,{" "}
            <span className="text-[var(--mint-green)]">simplified</span>
          </h2>

          <p className="mt-3 font-jakarta text-[14.5px] sm:text-[16px] leading-[1.65] text-[#9fa9a5] max-w-[620px] mx-auto">
            Every file moves through an automated, transparent verification and routing pipeline with real-time status visibility at every milestone.
          </p>
        </div>

        {/* ================= DESKTOP HORIZONTAL 7-STEP FLOW ================= */}
        <div className="relative hidden lg:block">
          
          {/* Continuous Animated Connecting Dashed Green Line */}
          <div className="absolute top-[34px] left-[7%] right-[7%] -translate-y-1/2 z-0">
            <svg
              className="w-full h-[6px] overflow-visible"
              viewBox="0 0 1000 6"
              fill="none"
              preserveAspectRatio="none"
            >
              {/* Static Background Guide */}
              <line
                x1="0"
                y1="3"
                x2="1000"
                y2="3"
                stroke="rgba(255,255,255,0.12)"
                strokeWidth="2"
                strokeDasharray="5 5"
              />

              {/* Foreground Animated Continuous Flowing Green Line */}
              <line
                x1="0"
                y1="3"
                x2="1000"
                y2="3"
                stroke="#2ee5a2"
                strokeWidth="2.5"
                strokeDasharray="5 5"
                strokeLinecap="round"
                className="step-flow-line-horizontal"
              />
            </svg>
          </div>

          {/* 7 Step Circular Nodes Grid */}
          <div className="grid grid-cols-7 gap-3 xl:gap-4 relative z-10">
            {stages.map((stage) => (
              <div key={stage.id} className="flex flex-col items-center text-center group min-w-0">
                
                {/* Frosted Glassmorphism Circular Node */}
                <div className="relative flex h-[68px] w-[68px] shrink-0 aspect-square items-center justify-center rounded-full bg-white/[0.08] backdrop-blur-md border border-white/20 shadow-[inset_0_1px_1px_rgba(255,255,255,0.25),0_8px_25px_rgba(0,0,0,0.35)] transition-all duration-300 group-hover:scale-110 group-hover:bg-white/[0.16] group-hover:border-[var(--mint-green)] group-hover:shadow-[inset_0_1px_2px_rgba(255,255,255,0.45),0_0_26px_rgba(46,229,162,0.4)]">
                  
                  {/* Subtle Inner Radial Sheen */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 rounded-full bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.25),transparent_60%)]"
                  />

                  {/* Mint Glowing Icon */}
                  <div className="relative z-10 transition-transform duration-300 group-hover:scale-108">
                    {stage.icon}
                  </div>
                </div>

                {/* Step Number */}
                <span className="mt-3.5 font-jakarta text-[11px] font-bold text-[var(--mint-green)] uppercase tracking-wider">
                  {stage.id}
                </span>

                {/* Step Title */}
                <h3 className="mt-1 font-bricolage text-[16px] font-semibold tracking-tight text-white group-hover:text-[var(--mint-green)] transition-colors duration-200">
                  {stage.title}
                </h3>

                {/* Step Description */}
                <p className="mt-1 font-jakarta text-[12px] leading-[1.5] text-[#9fa9a5] max-w-[145px]">
                  {stage.description}
                </p>
              </div>
            ))}
          </div>

        </div>

        {/* ================= TABLET & MOBILE VERTICAL STEP FLOW ================= */}
        <div className="lg:hidden relative max-w-[420px] mx-auto space-y-0">
          {stages.map((stage, idx) => (
            <div
              key={stage.id}
              className="relative flex items-start gap-4 pb-6 last:pb-0"
            >
              {/* Segment line: ONLY rendered between idx and idx+1 - stops cleanly at step 7 */}
              {idx < stages.length - 1 && (
                <div
                  aria-hidden="true"
                  className="absolute left-[26px] top-[48px] bottom-0 w-[2px] -translate-x-1/2 z-0 overflow-hidden"
                >
                  <svg
                    className="w-[4px] h-full overflow-visible"
                    preserveAspectRatio="none"
                  >
                    <line
                      x1="2"
                      y1="0"
                      x2="2"
                      y2="500"
                      stroke="#2ee5a2"
                      strokeWidth="2"
                      strokeDasharray="4 4"
                      className="step-flow-line-vertical"
                    />
                  </svg>
                </div>
              )}

              {/* Glassmorphism Circular Node (Guaranteed 1:1 Aspect Ratio) */}
              <div className="relative z-10 flex h-[52px] w-[52px] shrink-0 aspect-square items-center justify-center rounded-full bg-white/[0.08] backdrop-blur-md border border-white/20 shadow-[inset_0_1px_1px_rgba(255,255,255,0.25),0_6px_20px_rgba(0,0,0,0.3)]">
                {stage.icon}
              </div>

              {/* Text Info */}
              <div className="pt-1 min-w-0">
                <span className="font-jakarta text-[10.5px] font-bold text-[var(--mint-green)] uppercase tracking-wider">
                  STEP {stage.id}
                </span>
                <h3 className="font-bricolage text-[16px] font-semibold text-white">
                  {stage.title}
                </h3>
                <p className="mt-0.5 font-jakarta text-[12.5px] leading-[1.5] text-[#9fa9a5]">
                  {stage.description}
                </p>
              </div>
            </div>
          ))}
        </div>

      </div>

      {/* Continuous Dash Animation Keyframes */}
      <style>{`
        @keyframes flowHorizontalTrack {
          from {
            stroke-dashoffset: 40;
          }
          to {
            stroke-dashoffset: 0;
          }
        }
        @keyframes flowVerticalTrack {
          from {
            stroke-dashoffset: 40;
          }
          to {
            stroke-dashoffset: 0;
          }
        }
        .step-flow-line-horizontal {
          animation: flowHorizontalTrack 1.8s linear infinite;
        }
        .step-flow-line-vertical {
          animation: flowVerticalTrack 1.8s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .step-flow-line-horizontal,
          .step-flow-line-vertical {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
}

export default LeadTimeline;